import {  Accordion, AccordionDetails, AccordionSummary, Box, Divider, Stack, Typography } from '@mui/material';
import AreaLineChartWidget from './AreaLineChartWidget';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import Insights from 'components/DataEstate/Insights';
import { useState } from 'react';
import LoaderComponent from 'components/Loader';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ChangeIndicator } from './ChangeIndicator';
import { useTheme } from "@mui/material/styles";
import HighlightedText from 'components/GlobalSearch/HighlightText';
import FormattedNumber from './FormattedNumber';
import { InfoTooltip, TooltipLabel } from './InfoTooltip';
import InsightsSection from 'components/Common/InsightsSection';

interface Insight {
	id?: number;
	title: string;
	description: string;
}

// Define the component props
interface WidgetProps {
	isLoading: boolean;
	titleText: string;
	title: any;
	value: string | number;
	data?: any[];
	change: string | number;
	changeType: 'increase' | 'decrease' | string;
	colorMapping?: any;
	gradientMapping?: any;
	hasLegend?: any;
	handleEvent?: () => void;
	loader?: boolean;
	fetchInsights: () => void;
	insights: Insights[];
	setInsights: React.Dispatch<React.SetStateAction<Insights[]>>;
	onAdd: (insight: Insight) => void;
	onUpdate: (insight: Insight) => void;
	onDelete: (id: number) => void;
	customHeight?: any;
	openDrawer?: (titleText: string) => void;
	tooltipLabel?: TooltipLabel;
	hasTooltip?: boolean;
}

const AreaInfoWidget = ({
	isLoading = false,
	titleText ='',
	title = '',
	data,
	value,
	change,
	changeType,
	colorMapping,
	gradientMapping,
	hasLegend,
	handleEvent,
	loader,
	fetchInsights,
	insights,
	onAdd,
	onUpdate,
	onDelete,
	customHeight,
	openDrawer,
	tooltipLabel,
	hasTooltip = true
}: Readonly<WidgetProps>) => {
	const theme = useTheme();
	const colors = theme.palette;
	const [expanded, setExpanded] = useState(false);

	const handleAccordionChange = async (_event: React.SyntheticEvent, isExpanded: boolean) => {
		setExpanded(isExpanded);
		if (isExpanded) 
			fetchInsights(); 
	};
	
	return (
		<Box
			sx={{
				backgroundColor: colors.widget.background.card,
				display: 'flex',
				flexDirection: 'column',
				color: colors.widget.text.main,
				padding: 2,
				borderRadius: '4px',
			}}
		>
			<Box
				sx={{
					backgroundColor: colors.widget.background.card,
					display: 'flex',
					flexDirection: 'row',
					color: colors.widget.text.main,
					height: customHeight || '170px'
				}}
			>
				{/* Left Section (45%) */}
				<Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', flex: '20%',
					cursor: 'pointer' }} onClick = {handleEvent}>
					<Stack direction="row" justifyContent="space-between" minHeight="18px">
						<Typography
							variant='widgetTitle'
							sx={{ p: '0 !important', color: colors.widget.text.title }}
						>
							<HighlightedText text={title} />
						</Typography>
						{hasTooltip && !!tooltipLabel ? <InfoTooltip label={tooltipLabel} /> : null}
					</Stack>
					<Stack flexGrow={1} justifyContent="center" sx={{ mt: 1 }}>
						<Stack color={colors.widget.text.main} direction="column" alignItems="start">
							<Stack direction="row" alignItems="baseline" flexWrap="wrap">
								<FormattedNumber number={value} isCost={true} />
							</Stack>
						</Stack>
					</Stack>

					{/* Change Indicator */}
					<ChangeIndicator
						change={change}
						changeType={changeType}
						colors={colors}
					/>
					{/* <Box display="flex" alignItems="center" mt={1}></Box> */}
					{titleText !== 'Azure Cost' && (
						<Typography
							sx={{
							color: colors.widget.text.action.primary,
							cursor: 'pointer',
							mt: 1,
							display: 'inline-flex',
							alignItems: 'center',
							}}
							onClick={(e) => {
								e.stopPropagation()
								openDrawer?.(titleText)}
							}
						>
							VIEW BREAKUPS
							<ChevronRightIcon sx={{ color: colors.widget.text.action.primary}}/>
						</Typography>
					)}
				</Box>
				{/* Right Section (55%) */}
				<AreaLineChartWidget
					data={data}
					isLoading={isLoading}
					colorMapping={colorMapping}
					gradientMapping={gradientMapping}
					hasLegend = {hasLegend}
					handleEvent = {handleEvent}
				/>
			</Box>

			<Divider
				orientation="horizontal"
				flexItem
				sx={{
					my: 1,
					borderColor: colors.textLight.dark,
				}}
			/>
			<Box
				sx={{
					backgroundColor: `${colors.widget.background.card} !important`,
				}}
			>
				<Accordion
					expanded={expanded}
					onChange={handleAccordionChange}
					sx={{
						background: `${colors.widget.background.card} !important`,
						backgroundColor: `${colors.widget.background.card} !important`,
						boxShadow: 'none',
						'&.Mui-expanded': {
							background: `${colors.widget.background.card} !important`,
							backgroundColor: `${colors.widget.background.card} !important`,
						},
						'&::before': {
							display: 'none',
						},
						'&.MuiAccordion-root .MuiAccordionDetails-root': {
							backgroundColor: `${colors.widget.background.card} !important`,
							background: `${colors.widget.background.card} !important`,
							border: 'none !important'
						},
					}}
				>
					<AccordionSummary
						sx={{
							background: `${colors.widget.background.card} !important`,
							backgroundColor: `${colors.widget.background.card} !important`,
							color: colors.textLight.dark,
							p: 0,
							'&.Mui-expanded': {
								background: `${colors.widget.background.card} !important`,
								backgroundColor: `${colors.widget.background.card} !important`,
							},
							'& .MuiAccordionSummary-content': {
								background: `${colors.widget.background.card} !important`,
								backgroundColor: `${colors.widget.background.card} !important`,
							},
							'& .MuiAccordionSummary-content.Mui-expanded': {
								background: `${colors.widget.background.card} !important`,
								backgroundColor: `${colors.widget.background.card} !important`,
							},
						}}
					>
						<Box display="flex" alignItems="center">
							<Typography
								variant='subHeader'
								sx={{ color: `${colors.textLight.dark} !important` }}
							>
								Insights/Actions
							</Typography>
							{expanded ? (
								<ExpandLess sx={{ color: `${colors.textLight.dark} !important`, ml: 1 }} />
							) : (
								<ExpandMore sx={{ color: `${colors.textLight.dark} !important`, ml: 1 }} />
							)}
						</Box>
					</AccordionSummary>

					{loader ? (
						<LoaderComponent bgColor={colors.widget.background.card} />
					) : (
						<AccordionDetails>
							<InsightsSection
								insights={insights}
								onAdd={onAdd}
								onUpdate={onUpdate}
								onDelete={onDelete}
							/>
						</AccordionDetails>
					)}
				</Accordion>
			</Box>
		</Box>
	);
};

export default AreaInfoWidget;
